import { lazy, useEffect } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { GuardProvider } from 'react-router-guards'

import { RouteFactory } from './route-factory'
import { guardsRoutes } from './utils/guards-routes'

// layouts:
import {
  AuthLayout,
  StepsLayout,
  BaseLayout,
  ContentLayout,
  BaseLayoutEntry
} from '../layouts'

const Login = lazy(() => import('../../pages/auth/login/login'))
const SelectFarm = lazy(() => import('../../pages/select-farm/select-farm'))
const SelectCategory = lazy(() => import('../../pages/select-category/select-category'))

//const HomeUbere = lazy(() => import('../../pages/home-ubere/home-ubere'))
const HomeUbereV2 = lazy(() => import('../../pages/home-ubere/home-ubere-v2'))
const HomeBezerra = lazy(() => import('../../pages/home-bezerra/home-bezerra'))

const UbereCowRecord = lazy(() => import('../../pages/ubere/ubere-cow-record/cow-record'))
const UbereCowDatas = lazy(() => import('../../pages/ubere/ubere-cow-datas/ubere-cow-datas'))
const UbereDataImport = lazy(() => import('../../pages/ubere/ubere-data-import/ubere-data-import'))
const UbereResults = lazy(() => import('../../pages/ubere/ubere-results/ubere-results'))
const UbereResult = lazy(() => import('../../pages/ubere/ubere-result/ubere-result'))
const UbereTreatments = lazy(() => import('../../pages/ubere/ubere-treatments/ubere-treatments'))
const UbereNewTreatments = lazy(() => import('../../pages/ubere/ubere-new-treatments/ubere-new-treatments'))
const UbereAddTreatment = lazy(() => import('../../pages/ubere/ubere-add-treatments/ubere-add-treatments'))
const UbereTreatmentsDatas = lazy(() => import('../../pages/ubere/ubere-treatments-datas/ubere-treatments-datas'))
const UbereAnalysis = lazy(() => import('../../pages/ubere/ubere-analysis/ubere-analysis'))
const UbereAnalysisDatas = lazy(() => import('../../pages/ubere/ubere-analysis-datas/ubere-analysis-datas'))
const UbereGraphics = lazy(() => import('../../pages/ubere/ubere-graphics/ubere-graphics'))
const UbereDatasGraphicsIncidency = lazy(() => import('../../pages/ubere/ubere-datas-graphics/ubere-datas-graphics-incidency'))
const UbereDatasGraphicsGrowthFrequency = lazy(() => import('../../pages/ubere/ubere-datas-graphics/ubere-datas-graphics-growth-frequency'))
const UbereDatasGraphicsPrevalency = lazy(() => import('../../pages/ubere/ubere-datas-graphics/ubere-datas-graphics-prevalency'))
const UbereDatasGraphicsPrevalencyGram = lazy(() => import('../../pages/ubere/ubere-datas-graphics/ubere-datas-graphics-prevalency-gram'))
const ClinicalCure = lazy(() => import('../../pages/ubere/ubere-datas-graphics/clinical-cure'))
const ClinicalCureByDiagnostic = lazy(() => import('../../pages/ubere/ubere-datas-graphics/clinical-cure-by-diagnostic'))
const ClinicalCureByProtocol = lazy(() => import('../../pages/ubere/ubere-datas-graphics/clinical-cure-by-protocol'))
const MastiteClinical = lazy(() => import('../../pages/ubere/ubere-datas-graphics/mastite-clinical'))

const UbereCommunity = lazy(() => import('../../pages/ubere/ubere-community/ubere-community'))
const UbereBenchmarking = lazy(() => import('../../pages/ubere/ubere-benchmarking/ubere-benchmarking'))
const UbereTopIntramamarios = lazy(() => import('../../pages/ubere/ubere-top-5-intramamarios/ubere-top-5-intramamarios'))
const UbereTopInjetaveis = lazy(() => import('../../pages/ubere/ubere-top-5-injetaveis/ubere-top-5-injetaveis'))
const UbereTopAntiInflamatorios = lazy(() => import('../../pages/ubere/ubere-top-5-anti-inflamatorios/ubere-top-5-anti-inflamatorios'))
const UbereTopIntramamarioAgents = lazy(() => import('../../pages/ubere/ubere-top-5-intramamario-por-agentes/ubere-top-5-intramamario-por-agentes'))
const UbereDiasTratamentoIntramamarioAgents = lazy(() => import('../../pages/ubere/ubere-dias-tratamento-intramamario-agentes/ubere-dias-tratamento-intramamario-agentes'))
const UbereCuraClinica = lazy(() => import('../../pages/ubere/ubere-cura-clinica/ubere-cura-clinica'))
const UbereIncidenciaMastite = lazy(() => import('../../pages/ubere/ubere-incidencia-mastite/ubere-incidencia-mastite'))
const UbereReports = lazy(() => import('../../pages/ubere/ubere-reports/ubere-reports'))
const UbereReportsList = lazy(() => import('../../pages/ubere/ubere-reports/ubere-reports-list'))
const UbereLaunch = lazy(() => import('../../pages/ubere/ubere-launch/ubere-launch'))
const UbereMyTasks = lazy(() => import('../../pages/ubere/ubere-my-tasks/ubere-my-tasks'))
const UbereTask = lazy(() => import('../../pages/ubere/ubere-task/ubere-task'))
const UbereMicrobiologicalExamination = lazy(() => import('../../pages/ubere/ubere-microbiological-examination/ubere-microbiological-examination'))
const UbereMicrobiologicalExaminationSideB = lazy(() => import('../../pages/ubere/ubere-microbiological-examination-lado-b/ubere-microbiological-examination-lado-b'))
const UbereNewClinicalMastitis = lazy(() => import('../../pages/ubere/ubere-new-clinical-mastitis/ubere-new-clinical-mastitis'))
const UbereAnalyzeCbtTank = lazy(() => import('../../pages/ubere/ubere-analyze-cbt-tank/ubere-analyze-cbt-tank'))
const UbereReadingOfResults = lazy(() => import('../../pages/ubere/ubere-reading-of-results/ubere-reading-of-results'))
const UbereReviewAutomaticReadings = lazy(() => import('../../pages/ubere/ubere-review-automatic-readings/ubere-review-automatic-readings'))
const UbereMassLaunches = lazy(() => import('../../pages/ubere/ubere-mass-launches/ubere-mass-launches'))
const UbereBoardAttachment = lazy(() => import('../../pages/ubere/ubere-board-attachment/ubere-board-attachment'))
const UbereReprocessResult = lazy(() => import('../../pages/ubere/ubere-reprocess-result/ubere-reprocess-result'))
const UbereBoardAttachmentResult = lazy(() => import('../../pages/ubere/ubere-board-attachment-result/ubere-board-attachment-result'))
const UbereTreatmentsShare = lazy(() => import('../../pages/ubere/ubere-treatments-share/ubere-treatments-share'))

const BezerraColostrumBank = lazy(() => import('../../pages/bezerra/bezerra-colostrum-bank/bezerra-colostrum-bank'))
const BezerraColostrumBankDatas = lazy(() => import('../../pages/bezerra/bezerra-colostrum-bank-datas/bezerra-colostrum-bank-datas'))
const BezerraColostrum = lazy(() => import('../../pages/bezerra/bezerra-colostrum/bezerra-colostrum'))
const BezerraColostrumDatas = lazy(() => import('../../pages/bezerra/bezerra-colostrum-datas/bezerra-colostrum-datas'))
const BezerraTreatments = lazy(() => import('../../pages/bezerra/bezerra-treatments/bezerra-treatments'))
const BezerraNewTreatments = lazy(() => import('../../pages/bezerra/bezerra-new-treatments/bezerra-new-treatments'))
const BezerraAddTreatments = lazy(() => import('../../pages/bezerra/bezerra-add-treatments/bezerra-add-treatments'))
const BezerraTreatmentsShare = lazy(() => import('../../pages/bezerra/bezerra-treatments-share/bezerra-treatments-share'))
const BezerraIllness = lazy(() => import('../../pages/bezerra/bezerra-illness/bezerra-illness'))
const BezerraNewIllness = lazy(() => import('../../pages/bezerra/bezerra-new-illness/bezerra-new-illness'))
const BezerraIllnessDatas = lazy(() => import('../../pages/bezerra/bezerra-illness-datas/bezerra-illness-datas'))
const BezerraHeiferRecord = lazy(() => import('../../pages/bezerra/bezerra-heifer-record/bezerra-heifer-record'))
const BezerraHeiferData = lazy(() => import('../../pages/bezerra/bezerra-heifer-data/bezerra-heifer-data'))
const BezerraGraphics = lazy(() => import('../../pages/bezerra/bezerra-graphics/bezerra-graphics'))
const BezerraDatasGraphics = lazy(() => import('../../pages/bezerra/bezerra-datas-graphics/bezerra-datas-graphics'))
const BezerraGraphQualidadeColostro = lazy(() => import('../../pages/bezerra/bezerra-graph-qualidade-colostro/bezerra-graph-qualidade-colostro'))
const BezerraGraphQualidadeColostroBrix = lazy(() => import('../../pages/bezerra/bezerra-graph-qualidade-colostro-brix/bezerra-graph-qualidade-colostro-brix'))
const BezerraGraphQualidadeColostroLgGCBT = lazy(() => import('../../pages/bezerra/bezerra-graph-qualidade-colostro-lgG-CBT/bezerra-graph-qualidade-colostro-lgG-CBT'))
const BezerraGraphQualidadeColostragem = lazy(() => import('../../pages/bezerra/bezerra-graph-qualidade-colostragem/bezerra-graph-qualidade-colostragem'))
const BezerraLaunch = lazy(() => import('../../pages/bezerra/bezerra-launch/bezerra-launch'))
const BezerraReports = lazy(() => import('../../pages/bezerra/bezerra-reports/bezerra-reports'))
const BezerraReportsList = lazy(() => import('../../pages/bezerra/bezerra-reports/bezerra-reports-list'))
const BezerraMyTasks = lazy(() => import('../../pages/bezerra/bezerra-my-tasks/bezerra-my-tasks'))
const BezerraTask = lazy(() => import('../../pages/bezerra/bezerra-task/bezerra-task'))
const BezerraNewColostrum = lazy(() => import('../../pages/bezerra/bezerra-new-colostrum/bezerra-new-colostrum'))
const BezerraNewCowColostrum = lazy(() => import('../../pages/bezerra/bezerra-new-cow-colostrum/bezerra-new-cow-colostrum'))
const BezerraReadingOfResults = lazy(() => import('../../pages/bezerra/bezerra-reading-of-results/bezerra-reading-of-results'))

const UserData = lazy(() => import('../../pages/user-data/user-data'))
const FarmData = lazy(() => import('../../pages/farm-data/farm-data'))
const ChangePassword = lazy(() => import('../../pages/change-password/change-password'))
const ArticlesVideos = lazy(() => import('../../pages/articles-videos/articles-videos'))
const Video = lazy(() => import('../../pages/video/video'))
const Token = lazy(() => import('../../pages/token/token'))
const IntegrationPartners = lazy(() => import('../../pages/integration-partners/integration-partners'))
const NotificationCenter = lazy(() => import('../../pages/notification-center/notification-center'))
const Lactation = lazy(() => import('../../pages/lactation/lactation'))
const UpdateLactation = lazy(() => import('../../pages/update-lactation/update-lactation'))
const MyBills = lazy(() => import('../../pages/my-bills/my-bills'))
const Bill = lazy(() => import('../../pages/bill/bill'))
const OrderTracking = lazy(() => import('../../pages/order-tracking/order-tracking'))
const Order = lazy(() => import('../../pages/order/order'))
const Users = lazy(() => import('../../pages/users/users'))
const NewUsers = lazy(() => import('../../pages/new-users/new-users'))
const UserPermissions = lazy(() => import('../../pages/user-permissions/user-permissions'))
const Farm = lazy(() => import('../../pages/farm/farm'))
const NewFarm = lazy(() => import('../../pages/new-farm/new-farm'))

const Routes = () => {
  useEffect(() => {
    setInterval(() => {
      localStorage.setItem('refreshNotification', 'true')
    }, 5000)
  }, [])

  const routes = [
    {
      layout: AuthLayout,
      pages: [
        { path: '/login', component: Login }
      ]
    },
    {
      layout: StepsLayout,
      pages: [
        { path: '/selecionar-fazenda', component: SelectFarm },
        { path: '/selecionar-categoria', component: SelectCategory }
      ]
    },
    {
      layout: ContentLayout,
      pages: [
        // { path: '/bezerra/banco-de-colostro', component: BezerraColostrumBank },
        // { path: '/bezerra/dados-do-banco-de-colostro', component: BezerraColostrumBankDatas },
        // { path: '/bezerra/colostragem', component: BezerraColostrum },
        // { path: '/bezerra/dados-da-colostragem', component: BezerraColostrumDatas },
        // { path: '/bezerra/tratamentos', component: BezerraTreatments },
        // { path: '/bezerra/novo-tratamento', component: BezerraNewTreatments },
        // { path: '/bezerra/tratamento/:id', component: BezerraNewTreatments },
        // { path: '/bezerra/novo-tratamento', component: BezerraNewTreatments },
        // { path: '/bezerra/tratamentos/compartilhar/:id', component: BezerraTreatmentsShare },
        // { path: '/bezerra/adicionar-tratamento/:id', component: BezerraAddTreatments },
        // { path: '/bezerra/doencas', component: BezerraIllness },
        // { path: '/bezerra/nova-doenca', component: BezerraNewIllness },
        // { path: '/bezerra/nova-doenca/:id', component: BezerraNewIllness },
        // { path: '/bezerra/dados-das-doencas', component: BezerraIllnessDatas },
        // { path: '/bezerra/ficha-da-bezerra', component: BezerraHeiferRecord },
        // { path: '/bezerra/ficha-da-bezerra/:id', component: BezerraHeiferData },
        // { path: '/bezerra/graficos', component: BezerraGraphics },
        // { path: '/bezerra/relatorios', component: BezerraReports },
        // { path: '/bezerra/relatorios-processados', component: BezerraReportsList },
        // { path: '/bezerra/dados-dos-graficos', component: BezerraDatasGraphics },
        // { path: '/bezerra/qualidade-colostro', component: BezerraGraphQualidadeColostro },
        // { path: '/bezerra/qualidade-colostro-brix', component: BezerraGraphQualidadeColostroBrix },
        // { path: '/bezerra/qualidade-colostro-lgG-cbt', component: BezerraGraphQualidadeColostroLgGCBT },
        // { path: '/bezerra/qualidade-colostragem', component: BezerraGraphQualidadeColostragem },
        // { path: '/bezerra/minhas-tarefas', component: BezerraMyTasks },
        // { path: '/bezerra/tarefa/:id', component: BezerraTask },
        // { path: '/bezerra/novo-colostro', component: BezerraNewColostrum },
        // { path: '/bezerra/novo-colostro/:id', component: BezerraNewColostrum },
        // { path: '/bezerra/nova-colostragem', component: BezerraNewCowColostrum },
        // { path: '/bezerra/nova-colostragem/:id', component: BezerraNewCowColostrum },
        // { path: '/bezerra/leitura-de-resultados', component: BezerraReadingOfResults },
      ]
    },
    {
      layout: BaseLayoutEntry,
      pages: [
        // { path: '/ubere-lancamento', component: UbereLaunch }
      ]
    },
    {
      layout: BaseLayout,
      pages: [
        { path: '/', component: HomeUbereV2 },
        { path: '/ubere/ficha-da-vaca', component: UbereCowRecord },
        { path: '/ubere/ficha-da-vaca/:id', component: UbereCowDatas },
        { path: '/ubere/dados-da-vaca', component: UbereCowDatas },
        { path: '/ubere/importacao-de-dados', component: UbereDataImport },
        { path: '/ubere/resultados', component: UbereResults },
        { path: '/ubere/resultados/:id', component: UbereResult },
        { path: '/ubere/resultado', component: UbereResult },
        { path: '/ubere/tratamentos', component: UbereTreatments },
        { path: '/ubere/tratamentos/:id', component: UbereNewTreatments },
        { path: '/ubere/novo-tratamento', component: UbereNewTreatments },
        { path: '/ubere/adicionar-tratamento/:id', component: UbereAddTreatment },
        { path: '/ubere/dados-do-tratamentos', component: UbereTreatmentsDatas },
        { path: '/ubere/analise-cbt-tanque', component: UbereAnalysis },
        { path: '/ubere/analise-cbt-tanque/:id', component: UbereAnalysisDatas },
        { path: '/ubere/dados-da-analise-cbt-tanque', component: UbereAnalysisDatas },
        { path: '/ubere/relatorios', component: UbereReports },
        { path: '/ubere/relatorios-processados', component: UbereReportsList },
        { path: '/ubere/graficos', component: UbereGraphics },
        { path: '/ubere/grafico-ocorrencia-mastite-clinica', component: MastiteClinical },
        { path: '/ubere/grafico-incidencia-mastite-clinica', component: UbereDatasGraphicsIncidency },
        { path: '/ubere/grafico-frequencia-de-crescimento', component: UbereDatasGraphicsGrowthFrequency },
        { path: '/ubere/grafico-prevalencia-de-agentes', component: UbereDatasGraphicsPrevalency },
        { path: '/ubere/grafico-por-grupo-gram', component: UbereDatasGraphicsPrevalencyGram },
        { path: '/ubere/grafico-cura-clinica', component: ClinicalCure },
        { path: '/ubere/grafico-cura-clinica-por-tratamento', component: ClinicalCureByProtocol },
        { path: '/ubere/grafico-cura-clinica-por-diagnostico', component: ClinicalCureByDiagnostic },
        { path: '/ubere/comunidade-onfarmes', component: UbereCommunity },
        { path: '/ubere/onfarms-benchmarking', component: UbereBenchmarking },
        { path: '/ubere/top-5-intramamarios', component: UbereTopIntramamarios },
        { path: '/ubere/top-5-injetaveis', component: UbereTopInjetaveis },
        { path: '/ubere/top-5-anti-inflamatorios', component: UbereTopAntiInflamatorios },
        { path: '/ubere/top-5-intramamario-por-agentes', component: UbereTopIntramamarioAgents },
        { path: '/ubere/dias-tratamento-intramamario-por-agentes', component: UbereDiasTratamentoIntramamarioAgents },
        { path: '/ubere/cura-clinica', component: UbereCuraClinica },
        { path: '/ubere/incidencia-mastite-clinica', component: UbereIncidenciaMastite },
        { path: '/ubere/minhas-tarefas', component: UbereMyTasks },
        { path: '/ubere/tarefa/:id', component: UbereTask },
        { path: '/ubere/tarefa', component: UbereTask },
        { path: '/ubere/exame-microbiologico', component: UbereMicrobiologicalExamination },
        { path: '/ubere/exame-microbiologico/:id', component: UbereMicrobiologicalExamination },
        { path: '/ubere/exame-microbiologico-lado-b', component: UbereMicrobiologicalExaminationSideB },
        { path: '/ubere/nova-mastite-clinica-sem-exame', component: UbereNewClinicalMastitis },
        { path: '/ubere/nova-mastite-clinica-sem-exame/:id', component: UbereNewClinicalMastitis },
        { path: '/ubere/new-analise-cbt-tanque', component: UbereAnalyzeCbtTank },
        { path: '/ubere/leitura-de-resultados', component: UbereReadingOfResults },
        { path: '/ubere/leitura-de-resultados/:id', component: UbereReadingOfResults },
        { path: '/ubere/anexo-da-placa/:id', component: UbereBoardAttachment },
        { path: '/ubere/reprocessar/:id', component: UbereReprocessResult },
        { path: '/ubere/resultado-da-placa', component: UbereBoardAttachmentResult },
        { path: '/ubere/tratamentos/compartilhar/:id', component: UbereTreatmentsShare },
        { path: '/home-bezerra', component: HomeBezerra },
        { path: '/ubere-lancamento', component: UbereLaunch },
        { path: '/review-automatic-readings', component: UbereReviewAutomaticReadings },
        { path: '/mass-launches', component: UbereMassLaunches },
        { path: '/lactacao', component: Lactation },
        { path: '/lactacao/:id', component: UpdateLactation },
        { path: '/bezerra-lancamento', component: BezerraLaunch },
        { path: '/rastreio-de-pedidos', component: OrderTracking },
        { path: '/rastreio-de-pedidos/:id', component: Order },
        { path: '/nova-lactacao', component: UpdateLactation },
        { path: '/artigos-e-videos', component: ArticlesVideos },
        { path: '/video', component: Video },
        { path: '/video/:id', component: Video },
        { path: '/usuarios', component: Users },
        { path: '/usuarios/:uuid', component: NewUsers },
        { path: '/novo-usuario', component: NewUsers },
        { path: '/permissoes-usuarios', component: UserPermissions },
        { path: '/fazendas', component: Farm },
        { path: '/fazendas/:uuid', component: NewFarm },
        { path: '/nova-fazenda', component: NewFarm },
        { path: '/token', component: Token },
        { path: '/integracao-parceiros', component: IntegrationPartners },
        { path: '/central-de-notificacoes', component: NotificationCenter },
        { path: '/meus-dados/dados-do-usuario', component: UserData },
        { path: '/meus-dados/dados-da-fazenda', component: FarmData },
        { path: '/meus-dados/alterar-senha', component: ChangePassword },
        { path: '/minhas-contas', component: MyBills },
        { path: '/minhas-contas/:id', component: Bill }
      ]
    }
  ]

  return (
    <BrowserRouter>
      <GuardProvider guards={[guardsRoutes]} error={() => <h1>Error</h1>}>
        <RouteFactory routes={routes} />
      </GuardProvider>
    </BrowserRouter>
  )
}

export default Routes
